<template>
  <v-card :loading="loading" outlined class="payment-method-card">
    <div v-if="hideTitle" class="payment-header">
      <div>
        <div class="page-title">Payment Methods</div>
        <div class="page-subtitle">
          Payment collected by method for selected date range.
        </div>
      </div>
    </div>

    <div class="payment-filter-bar">
      <v-spacer />

      <DateTimePicker
        defaultRange="today"
        :range="true"
        v-model="dateTime"
        @submit="loadData"
      />
    </div>

    <v-card-text class="pa-4">
      <div v-if="!loading && chartTotal > 0" class="summary-row mb-4">
        <div class="summary-box">
          <div class="summary-label">Total Collected</div>
          <div class="summary-value">{{ chartTotal | currency }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Methods</div>
          <div class="summary-value">{{ methodCount }}</div>
        </div>
      </div>

      <div v-if="!loading && chartTotal === 0" class="empty-state">
        <v-icon size="42" color="grey">mdi-cash-remove</v-icon>
        <div class="empty-title">No payments found</div>
        <div class="empty-subtitle">Try another date range.</div>
      </div>

      <div v-show="chartTotal > 0" id="payment-method-chart"></div>
    </v-card-text>
  </v-card>
</template>

<script>
import paymentService from "@/modules/Customer/Payment/service";
import Highcharts from "highcharts";

const DateTimePicker = () => import("@/components/DateTimePicker");

export default {
  name: "payment-method-chart",

  props: {
    hideTitle: {
      default: true,
    },
    start: {
      default: null,
    },
    end: {
      default: null,
    },
  },

  components: {
    DateTimePicker,
  },

  data() {
    return {
      dateTime: {
        start: this.start || this.$moment().startOf("day").toISOString(),
        end: this.end || this.$moment().endOf("day").toISOString(),
      },
      loading: false,
      chartTotal: 0,
      methodCount: 0,
    };
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;

      const where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
      };

      try {
        const response = await paymentService.groupByPaymentMethod(where);

        const rows = response.data || [];

        const chartData = rows
          .map((row) => {
            return [
              row.paymentMethod || "Unknown",
              Number(row.totalPayment || 0),
            ];
          })
          .filter((row) => row[1] > 0)
          .sort((a, b) => b[1] - a[1]);

        this.chartTotal = chartData.reduce((sum, row) => {
          return sum + Number(row[1] || 0);
        }, 0);

        this.methodCount = chartData.length;

        if (this.chartTotal === 0) {
          return;
        }

        this.$nextTick(() => {
          Highcharts.chart("payment-method-chart", {
            credits: false,
            chart: {
              type: "column",
              backgroundColor: "transparent",
              height: 360,
            },
            title: {
              text: null,
            },
            xAxis: {
              type: "category",
              labels: {
                rotation: -35,
                style: {
                  fontSize: "12px",
                },
              },
            },
            yAxis: {
              title: {
                text: "Collected",
              },
              labels: {
                formatter: function () {
                  return "$" + Highcharts.numberFormat(this.value, 0);
                },
              },
            },
            legend: {
              enabled: false,
            },
            tooltip: {
              pointFormat: "Collected: <b>${point.y:,.2f}</b>",
            },
            plotOptions: {
              column: {
                borderRadius: 6,
                pointPadding: 0.12,
                groupPadding: 0.08,
              },
            },
            series: [
              {
                name: "Payment",
                data: chartData,
                color: "#FBC02D",
                dataLabels: {
                  enabled: true,
                  format: "${point.y:,.2f}",
                  style: {
                    fontSize: "12px",
                    textOutline: "none",
                  },
                },
              },
            ],
          });
        });
      } catch (error) {
        console.error("payment method chart failed", error);
        this.chartTotal = 0;
        this.methodCount = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-method-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.payment-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.payment-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.empty-state {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
}

.empty-title {
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 10px;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

#payment-method-chart {
  width: 100%;
  min-height: 360px;
}

@media (max-width: 600px) {
  .summary-row {
    grid-template-columns: 1fr;
  }

  .payment-filter-bar {
    align-items: stretch;
  }
}
</style>